import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import accountingApi from '../../services/accountingApi';

function IncomeExpense() {
    const [monthlyData, setMonthlyData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadIncomeExpense();
    }, []);

    const loadIncomeExpense = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await accountingApi.getIncomeExpense();

            if (response.success) {
                setMonthlyData(response.data || []);
            } else {
                setError(response.message || 'Failed to load income/expense data');
            }
        } catch (err) {
            console.error('Error loading income/expense:', err);
            setError('An error occurred while loading the income/expense data');
        } finally {
            setLoading(false);
        }
    };

    const maxValue = Math.max(...monthlyData.map(d => Math.max(d.income, d.expense)));

    return (
        <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                    <Link to="/accounting" className="p-2 text-slate-400 hover:text-indigo-600 rounded-lg transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </Link>
                    <h1 className="text-2xl font-bold text-slate-800">Income vs Expense</h1>
                </div>
                <p className="text-sm text-slate-600">Monthly income and expense comparison for {new Date().getFullYear()}</p>
            </div>

            {loading && (
                <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    <p className="mt-2 text-slate-600">Loading data...</p>
                </div>
            )}

            {error && (
                <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-md mb-6">
                    <p className="text-sm text-rose-700">{error}</p>
                </div>
            )}

            {!loading && monthlyData.length > 0 && (
                <>
                    {/* Chart */}
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 mb-6">
                        <div className="flex items-end justify-between gap-4 h-80">
                            {monthlyData.map((data, index) => {
                                const incomeHeight = maxValue > 0 ? (data.income / maxValue) * 100 : 0;
                                const expenseHeight = maxValue > 0 ? (data.expense / maxValue) * 100 : 0;

                                return (
                                    <div key={index} className="flex-1 flex flex-col items-center gap-2">
                                        <div className="w-full flex gap-1 items-end h-64">
                                            <div
                                                className="flex-1 bg-emerald-500 rounded-t transition-all hover:bg-emerald-600 relative group"
                                                style={{ height: `${incomeHeight}%` }}
                                            >
                                                <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                                    ₹{data.income.toLocaleString()}
                                                </div>
                                            </div>
                                            <div
                                                className="flex-1 bg-rose-500 rounded-t transition-all hover:bg-rose-600 relative group"
                                                style={{ height: `${expenseHeight}%` }}
                                            >
                                                <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                                    ₹{data.expense.toLocaleString()}
                                                </div>
                                            </div>
                                        </div>
                                        <span className="text-xs font-medium text-slate-600">{data.month}</span>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex justify-center gap-6 mt-6 pt-6 border-t border-slate-200">
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 bg-emerald-500 rounded"></div>
                                <span className="text-sm text-slate-600">Income</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 bg-rose-500 rounded"></div>
                                <span className="text-sm text-slate-600">Expense</span>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Month</th>
                                        <th className="px-6 py-3 text-right text-xs font-bold text-slate-500 uppercase">Income (₹)</th>
                                        <th className="px-6 py-3 text-right text-xs font-bold text-slate-500 uppercase">Expense (₹)</th>
                                        <th className="px-6 py-3 text-right text-xs font-bold text-slate-500 uppercase">Net (₹)</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {monthlyData.map((data, index) => {
                                        const net = data.income - data.expense;
                                        return (
                                            <tr key={index} className="hover:bg-slate-50">
                                                <td className="px-6 py-4 text-sm font-medium text-slate-900">{data.month}</td>
                                                <td className="px-6 py-4 text-sm text-right font-medium text-emerald-600">
                                                    ₹{data.income.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-right font-medium text-rose-600">
                                                    ₹{data.expense.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                </td>
                                                <td className={`px-6 py-4 text-sm text-right font-bold ${net >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                                    ₹{net.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default IncomeExpense;
